/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link, useDateFormatter, useLocale } from "@i18n/next";
import { useLocalStorageState, weekStartsOn } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  IconButton,
  ModalDropdownMenuItem,
  ScrollArea,
  spacing,
  Text,
} from "@packages/ui";
import {
  addDays,
  addMonths,
  endOfMonth,
  getMonth,
  getYear,
  isBefore,
  isFirstDayOfMonth,
  isSameDay,
  isSameMonth,
  parse,
  setMonth,
  setYear,
  startOfMonth,
  subMonths,
} from "date-fns";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BIBLE_DESKTOP_WIDTH, isCustomDesktop } from "../bibleStyle.constants";

const dayLabel: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});
const buttons: Interpolation = {
  alignItems: "center",
  columnGap: spacing.s,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  justifyContent: "center",
};
const yearHeader: Interpolation = {
  gridColumn: "span 7",
  textAlign: "center",
};
const dateSectionHeader: Interpolation = {
  display: "flex",
  gridColumn: "span 7",
  marginTop: spacing.negativeM,
  paddingLeft: spacing.m,
  paddingTop: spacing.m,
  placeItems: "center",
  [isCustomDesktop]: {
    display: "revert",
    paddingLeft: "revert",
    placeItems: "revert",
  },
};
const dateItem: Interpolation = {
  justifyContent: "center",
  minWidth: "initial",
  placeSelf: "center",
};
const dates: Interpolation = {
  alignItems: "center",
  display: "grid",
  gridAutoRows: "minmax(56px, min-content)",
  gridTemplateColumns: "repeat(7, minmax(48px, 1fr))",
  justifyContent: "space-evenly",
  [isCustomDesktop]: {
    gridTemplateColumns: "repeat(7, minmax(64px, 1fr))",
  },
};

type DailyReadingNavigatorProps = {
  currentDate: Date;
  className?: string;
};

export const DailyReadingNavigator = ({
  currentDate,
  className = "",
}: DailyReadingNavigatorProps) => {
  const [targetMiddle, setTargetMiddle] = useState<Date>(new Date());
  const locale = useLocale();
  const selectedMonthRef = useRef<HTMLHeadingElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [isQA] = useLocalStorageState({
    defaultValue: false,
    key: "isQA",
  }); // TODO: use Mixpanel feature flag
  const format = useDateFormatter();

  const dayInfo = (date: Date): { day: string; label: string } => ({
    day: format(date, "cccc"),
    label: format(date, "ccccc"),
  });

  const weekdayLabels = useMemo(() => {
    const sundayDate = parse("2023-12-31", "yyyy-MM-dd", new Date());

    const sunday = dayInfo(sundayDate);
    const monday = dayInfo(addDays(sundayDate, 1));
    const tuesday = dayInfo(addDays(sundayDate, 2));
    const wednesday = dayInfo(addDays(sundayDate, 3));
    const thursday = dayInfo(addDays(sundayDate, 4));
    const friday = dayInfo(addDays(sundayDate, 5));
    const saturday = dayInfo(addDays(sundayDate, 6));

    const startOfWeek = weekStartsOn(locale);

    const otherDays = [tuesday, wednesday, thursday, friday];

    if (startOfWeek === "sat") {
      return [saturday, sunday, monday, ...otherDays];
    }
    if (startOfWeek === "sun") {
      return [sunday, monday, ...otherDays, saturday];
    }
    return [monday, ...otherDays, saturday, sunday];
  }, []);

  useEffect(() => {
    if (selectedMonthRef.current) {
      selectedMonthRef.current.scrollIntoView({ block: "start" });
    } else {
      setTimeout(() => {
        if (selectedMonthRef.current) {
          selectedMonthRef.current.scrollIntoView({ block: "start" });
        }
      }, 10);
    }
  }, []);

  const pieces: JSX.Element[] = [];

  let date = new Date();
  date = setMonth(date, getMonth(targetMiddle));
  date = setYear(date, getYear(targetMiddle));
  const endDate = endOfMonth(addMonths(date, 1));
  date = startOfMonth(subMonths(date, 1));
  const startYear = getYear(date);
  while (isBefore(date, endDate) || isSameDay(date, endDate)) {
    if (isFirstDayOfMonth(date)) {
      if (getYear(date) !== startYear && getMonth(date) === 0) {
        pieces.push(
          <Text
            key={`year_title_${getYear(date)}`}
            as={"h1"}
            type={"title"}
            size={"xl"}
            css={yearHeader}
          >
            {/*format(date, "yyyy")*/}
            {getYear(date)}
          </Text>,
        );
      }
      pieces.push(
        <Text
          key={`month_title_${format(date, "MMMM")}`}
          ref={isSameMonth(currentDate, date) ? selectedMonthRef : null}
          as={"h2"}
          type={"title"}
          size={"l"}
          css={dateSectionHeader}
        >
          {format(date, "MMMM")}
        </Text>,
      );
      pieces.push(
        ...weekdayLabels.map((label) => (
          <Text
            as={"h3"}
            type={"title"}
            size={"s"}
            key={`month_${format(date, "MMMM")}_weekday_${label.day}`}
            css={[dayLabel, dateItem]}
          >
            {label.label}
          </Text>
        )),
      );
      const day1Idx = weekdayLabels.findIndex(
        (label) => label.day === format(date, "cccc"),
      );
      for (let i = 0; i < day1Idx; i++) {
        // empty spaces
        pieces.push(<div />);
      }
    }

    pieces.push(
      <Link href={`/bible/daily-readings/${format(date, "yyyy-MM-dd")}`}>
        <ModalDropdownMenuItem
          key={`reading_${format(date, "yyyy-MM-dd")}_option`}
          selected={isSameDay(currentDate, date)}
          dropdownMenuItemProps={{
            hideCheckOnSelected: true,
          }}
          dropdownMenuItemCSS={dateItem}
          modalItemProps={{
            hideCheckOnSelected: true,
          }}
          modalItemCSS={dateItem}
          minDesktopWidth={BIBLE_DESKTOP_WIDTH}
        >
          <>{date.getDate()}</>
        </ModalDropdownMenuItem>
      </Link>,
    );
    date = addDays(date, 1);
  }

  const handleLeft = useCallback(() => {
    setTargetMiddle(subMonths(targetMiddle, 2));
    scrollerRef.current?.scrollTo({ top: 0 });
  }, [targetMiddle]);

  const handleRight = useCallback(() => {
    setTargetMiddle(addMonths(targetMiddle, 2));
    scrollerRef.current?.scrollTo({ top: 0 });
  }, [targetMiddle]);

  const GroupPicker = useCallback(
    ({ position }: { position: "top" | "bottom" }) => {
      return (
        <div key={`${position}YearButtons`} css={buttons}>
          <IconButton
            icon={<ArrowLeftIcon />}
            onClick={handleLeft}
            variant={"transparentPrimary"}
          />
          <Text as={"h1"} type={"title"} size={"l"}>
            {`${format(subMonths(targetMiddle, 1), "LLL yyyy")} - ${format(addMonths(targetMiddle, 1), "LLL yyyy")}`}
          </Text>
          <IconButton
            icon={<ArrowRightIcon />}
            onClick={handleRight}
            variant={"transparentPrimary"}
          />
        </div>
      );
    },
    [targetMiddle, handleLeft, handleRight],
  );

  return (
    <ScrollArea viewportProps={{ className }} ref={scrollerRef}>
      {isQA ? <GroupPicker key={"topYearPicker"} position={"top"} /> : null}
      <div css={dates}>{pieces}</div>
      {isQA ? (
        <GroupPicker key={"bottomYearPicker"} position={"bottom"} />
      ) : null}
    </ScrollArea>
  );
};
